import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Upload, FileText, Heart, Save, CheckCircle2, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";
import { HEALTH_CONDITIONS } from "@/lib/conditions";

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function Settings() {
    const { user, setUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialTab =
        searchParams.get("tab") === "health" ? "health" : "profile";
    const onboardingFlow = searchParams.get("onboarding") === "1";
    const [tab, setTab] = useState(initialTab);
    const [name, setName] = useState(user?.name || "");
    const [savingProfile, setSavingProfile] = useState(false);

    const [mode, setMode] = useState("description");
    const [description, setDescription] = useState("");
    const [conditions, setConditions] = useState([]);
    const [fileInfo, setFileInfo] = useState(null); // {name, mime, has}
    const [pendingFile, setPendingFile] = useState(null); // base64 data url
    const [pendingMeta, setPendingMeta] = useState(null);
    const [savingHealth, setSavingHealth] = useState(false);
    const [drag, setDrag] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/health-profile");
                setMode(r.data.mode || "description");
                setDescription(r.data.description || "");
                setConditions(r.data.conditions || []);
                if (r.data.has_blood_test) {
                    setFileInfo({
                        name: r.data.blood_test_filename || "Uploaded file",
                        mime: r.data.blood_test_mime,
                        has: true,
                    });
                }
            } catch {
                // ignore
            }
        })();
    }, []);

    const saveProfile = async () => {
        setSavingProfile(true);
        const wasOnboarding = onboardingFlow || !user?.onboarded_profile;
        try {
            const r = await api.put("/profile", { name });
            setUser(r.data);
            if (wasOnboarding) {
                toast.success("Profile saved · now add your health context");
                setTab("health");
                setSearchParams(
                    { tab: "health", onboarding: "1" },
                    { replace: true }
                );
            } else {
                toast.success("Profile updated");
            }
        } catch {
            toast.error("Could not update profile");
        } finally {
            setSavingProfile(false);
        }
    };

    const onFile = async (file) => {
        if (!file) return;
        const ok =
            file.type.startsWith("image/") || file.type === "application/pdf";
        if (!ok) {
            toast.error("Upload an image or PDF");
            return;
        }
        const b64 = await fileToBase64(file);
        setPendingFile(b64);
        setPendingMeta({ name: file.name, mime: file.type });
        setFileInfo({ name: file.name, mime: file.type, has: true });
    };

    const saveHealth = async () => {
        setSavingHealth(true);
        try {
            const payload = {
                mode,
                description: description || null,
                conditions: conditions,
            };
            if (pendingFile) {
                payload.blood_test_file_base64 = pendingFile;
                payload.blood_test_mime = pendingMeta?.mime;
                payload.blood_test_filename = pendingMeta?.name;
            }
            await api.put("/health-profile", payload);
            setPendingFile(null);
            setPendingMeta(null);
            if (onboardingFlow) {
                toast.success("All set · ready to scan");
                navigate("/dashboard");
            } else {
                toast.success("Health profile saved");
            }
        } catch {
            toast.error("Could not save health profile");
        } finally {
            setSavingHealth(false);
        }
    };

    const toggleCondition = (id) => {
        setConditions((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
    };

    const clearFile = async () => {
        setPendingFile(null);
        setPendingMeta(null);
        setFileInfo(null);
        try {
            await api.put("/health-profile", {
                mode,
                description,
                blood_test_file_base64: "",
                blood_test_mime: "",
                blood_test_filename: "",
            });
            toast.success("Removed file");
        } catch {
            toast.error("Could not remove file");
        }
    };

    return (
        <Shell>
            <div className="nl-fade-up">
                <p className="font-eyebrow mb-2">Settings</p>
                <h1
                    className="font-serif-editorial text-4xl sm:text-5xl leading-[1.02] tracking-tight"
                    style={{ color: "#1A3626" }}
                >
                    Personalize
                    <br />
                    <em className="italic font-normal">your lens.</em>
                </h1>

                <Tabs
                    value={tab}
                    onValueChange={(v) => {
                        setTab(v);
                        setSearchParams(v === "health" ? { tab: "health" } : {}, {
                            replace: true,
                        });
                    }}
                    className="mt-8"
                >
                    <TabsList className="bg-[#EAF0E9] rounded-full p-1 h-auto">
                        <TabsTrigger
                            value="profile"
                            data-testid="tab-profile"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1A3626] text-[#4A5A4E]"
                        >
                            Profile
                        </TabsTrigger>
                        <TabsTrigger
                            value="health"
                            data-testid="tab-health"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1A3626] text-[#4A5A4E]"
                        >
                            Health
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="profile" className="mt-6">
                        <div className="nl-card space-y-5">
                            <div className="flex items-center gap-4">
                                {user?.picture ? (
                                    <img
                                        src={user.picture}
                                        alt={user.name}
                                        className="w-16 h-16 rounded-full object-cover border border-[#1A3626]/10"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-[#EAF0E9] flex items-center justify-center text-xl text-[#1A3626]">
                                        {user?.name?.[0]?.toUpperCase() ?? "?"}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <p className="font-serif-editorial text-xl text-[#1A3626] truncate">
                                        {user?.name}
                                    </p>
                                    <p className="text-sm text-[#4A5A4E] truncate" data-testid="profile-email">
                                        {user?.email}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label className="font-eyebrow mb-2 block">Display name</label>
                                <input
                                    className="nl-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    data-testid="profile-name-input"
                                />
                            </div>

                            <button
                                onClick={saveProfile}
                                disabled={savingProfile || !name.trim()}
                                className="nl-btn-primary"
                                data-testid="profile-save-btn"
                            >
                                <Save className="w-4 h-4" />
                                {savingProfile
                                    ? "Saving…"
                                    : onboardingFlow || !user?.onboarded_profile
                                        ? "Save & continue →"
                                        : "Save changes"}
                            </button>
                        </div>
                    </TabsContent>

                    <TabsContent value="health" className="mt-6">
                        <div className="nl-card space-y-5">
                            <div className="flex items-start gap-3">
                                <Heart className="w-5 h-5 mt-1" style={{ color: "#3B824E" }} />
                                <div>
                                    <h3 className="font-serif-editorial text-xl text-[#1A3626]">
                                        How should we tailor your scores?
                                    </h3>
                                    <p className="text-sm text-[#4A5A4E]">
                                        Describe your health, upload recent blood test, or both.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { k: "description", label: "Describe" },
                                    { k: "upload", label: "Upload" },
                                    { k: "both", label: "Both" },
                                ].map((o) => (
                                    <button
                                        key={o.k}
                                        data-testid={`health-mode-${o.k}`}
                                        onClick={() => setMode(o.k)}
                                        className={
                                            "rounded-full px-4 py-1.5 text-sm transition-colors " +
                                            (mode === o.k
                                                ? "bg-[#1A3626] text-[#F9F9F6]"
                                                : "bg-[#EAF0E9] text-[#1A3626] hover:bg-[#D5E1D3]")
                                        }
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>

                            {(mode === "description" || mode === "both") && (
                                <div>
                                    <label className="font-eyebrow mb-2 block">
                                        Health description (BP, A1C, meds, allergies…)
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={5}
                                        placeholder="e.g. BP 138/86, A1C 6.2, total cholesterol 215, on metformin 500mg, watching sodium, lactose intolerant…"
                                        className="nl-input resize-none"
                                        data-testid="health-description-input"
                                    />
                                </div>
                            )}

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="font-eyebrow flex items-center gap-1.5">
                                        <Activity className="w-3.5 h-3.5" /> Conditions
                                    </label>
                                    {conditions.length > 0 && (
                                        <span className="text-[11px] text-[#3B824E]">
                                            {conditions.length} selected
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                                    data-testid="health-conditions-grid"
                                >
                                    {HEALTH_CONDITIONS.map((c) => {
                                        const checked = conditions.includes(c.id);
                                        return (
                                            <button
                                                type="button"
                                                key={c.id}
                                                onClick={() => toggleCondition(c.id)}
                                                data-testid={`condition-${c.id}`}
                                                className={
                                                    "flex items-center gap-2.5 px-4 py-3 rounded-2xl text-sm text-left transition-all border " +
                                                    (checked
                                                        ? "bg-[#1A3626] text-[#F9F9F6] border-transparent"
                                                        : "bg-[#F9F9F6] text-[#1A3626] border-[#1A3626]/10 hover:border-[#3B824E]/40")
                                                }
                                            >
                                                <span
                                                    className={
                                                        "w-4 h-4 rounded-md flex items-center justify-center border shrink-0 " +
                                                        (checked
                                                            ? "bg-[#3B824E] border-[#3B824E]"
                                                            : "bg-white border-[#1A3626]/20")
                                                    }
                                                >
                                                    {checked && (
                                                        <CheckCircle2 className="w-3 h-3 text-white" />
                                                    )}
                                                </span>
                                                <span className="truncate">{c.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[11px] text-[#8BA092] mt-2">
                                    Tap any that apply. We'll factor these into every scan.
                                </p>
                            </div>

                            {(mode === "upload" || mode === "both") && (
                                <div>
                                    <label className="font-eyebrow mb-2 block">
                                        Blood test (image or PDF)
                                    </label>
                                    {fileInfo?.has ? (
                                        <div
                                            className="nl-card !p-4 flex items-center gap-3 !bg-[#EAF0E9]"
                                            data-testid="health-file-present"
                                        >
                                            <CheckCircle2
                                                className="w-5 h-5 shrink-0"
                                                style={{ color: "#3B824E" }}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-[#1A3626] truncate">
                                                    {fileInfo.name}
                                                </p>
                                                <p className="text-xs text-[#4A5A4E]">
                                                    {fileInfo.mime}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => fileRef.current?.click()}
                                                className="text-xs text-[#3B824E] hover:underline"
                                                data-testid="health-file-replace"
                                            >
                                                Replace
                                            </button>
                                            <button
                                                onClick={clearFile}
                                                className="text-[#AD4A42] p-1.5 hover:bg-white/60 rounded-full"
                                                data-testid="health-file-remove"
                                                aria-label="Remove file"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => fileRef.current?.click()}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDrag(true);
                                            }}
                                            onDragLeave={() => setDrag(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDrag(false);
                                                onFile(e.dataTransfer.files?.[0]);
                                            }}
                                            className={`dashed-drop ${drag ? "drag" : ""} px-6 py-10 text-center cursor-pointer`}
                                            data-testid="health-file-dropzone"
                                        >
                                            <Upload
                                                className="w-8 h-8 mx-auto mb-2"
                                                style={{ color: "#3B824E" }}
                                            />
                                            <p className="text-[#1A3626] font-medium">
                                                Drag & drop, or click to upload
                                            </p>
                                            <p className="text-xs text-[#8BA092] mt-1">
                                                Image (JPEG/PNG) or PDF · stored privately
                                            </p>
                                        </div>
                                    )}
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="hidden"
                                        onChange={(e) => onFile(e.target.files?.[0])}
                                        data-testid="health-file-input"
                                    />
                                </div>
                            )}

                            <button
                                onClick={saveHealth}
                                disabled={savingHealth}
                                className="nl-btn-primary"
                                data-testid="health-save-btn"
                            >
                                <Save className="w-4 h-4" />
                                {savingHealth
                                    ? "Saving…"
                                    : onboardingFlow
                                        ? "Save & start scanning →"
                                        : "Save health profile"}
                            </button>

                            <div className="flex items-start gap-2 text-xs text-[#8BA092]">
                                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <p>
                                    PDFs are stored but only image blood tests are visually
                                    interpreted. For PDFs, include key values in the description
                                    for best results.
                                </p>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Shell>
    );
}
